import { useEffect, useCallback } from 'react';
import socketService, { SocketEventType, ProductEvent } from '../services/socket';

/** Hook generico che restituisce on/off del socketService */
export const useSocketEvents = () => {
  useEffect(() => {
    socketService.connect();
    return () => { socketService.disconnect(); };
  }, []);

  const on = useCallback((event: string, cb: Function) => {
    socketService.on(event, cb);
    return () => socketService.off(event, cb);
  }, []);

  const off = useCallback((event: string, cb: Function) => {
    socketService.off(event, cb);
  }, []);

  return { on, off };
};

interface UseSocketOptions {
  autoConnect?: boolean;
}

export const useSocket = (options: UseSocketOptions = { autoConnect: true }) => {
  useEffect(() => {
    if (options.autoConnect) {
      socketService.connect();
    }

    return () => {
      if (options.autoConnect) {
        socketService.disconnect();
      }
    };
  }, [options.autoConnect]);

  const addEventListener = useCallback((event: SocketEventType, callback: (data: ProductEvent) => void) => {
    socketService.on(event, callback);
    
    return () => {
      socketService.off(event, callback);
    };
  }, []);

  const isConnected = useCallback(() => {
    return socketService.isConnected();
  }, []);

  const connect = useCallback(() => {
    socketService.connect();
  }, []);

  const disconnect = useCallback(() => {
    socketService.disconnect();
  }, []);

  return {
    addEventListener,
    isConnected,
    connect,
    disconnect
  };
};

// Hook specifico per gli eventi dei prodotti e menu personalizzati
export const useProductEvents = (callbacks: {
  onProductAdded?: (data: ProductEvent) => void;
  onProductUpdated?: (data: ProductEvent) => void;
  onProductDeleted?: (data: ProductEvent) => void;
  onCustomMenuUpdated?: () => void;
}) => {
  const { addEventListener } = useSocket();

  useEffect(() => {
    const cleanupFunctions: (() => void)[] = [];

    if (callbacks.onProductAdded) {
      cleanupFunctions.push(addEventListener('product_added', callbacks.onProductAdded));
    }
    if (callbacks.onProductUpdated) {
      cleanupFunctions.push(addEventListener('product_updated', callbacks.onProductUpdated));
    }
    if (callbacks.onProductDeleted) {
      cleanupFunctions.push(addEventListener('product_deleted', callbacks.onProductDeleted));
    }
    if (callbacks.onCustomMenuUpdated) {
      cleanupFunctions.push(addEventListener('custom_menu_updated', callbacks.onCustomMenuUpdated));
    }

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [addEventListener, callbacks.onProductAdded, callbacks.onProductUpdated, callbacks.onProductDeleted, callbacks.onCustomMenuUpdated]);
};